import { useRef, useState } from "react";
import { BookOpen, UploadCloud, Loader2, Trash2, Save, AlertTriangle, Shapes, Link2, Images } from "lucide-react";
import { CatalogueDetails } from "@/components/CatalogueDetails";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useCatalogues } from "@/context/CatalogueContext";

const CODE_KEYS = [["frame", "Frame"], ["sill", "Sill"], ["mullion", "Mullion"], ["transom", "Transom"], ["sash", "Sash"], ["door_leaf", "Door leaf"], ["bead", "Bead"], ["interlock", "Interlock"]];
const inp = "h-7 w-full px-1.5 rounded bg-[#0B0F19] border border-slate-700 font-mono text-[11px] text-slate-100 focus:outline-none focus:border-cyan-500";

const ShapeThumb = ({ member, sh }) => {
  const pad = Math.max(sh.width, sh.height) * 0.08;
  const pts = (p) => p.map((q) => q.join(",")).join(" ");
  return (
    <div className="w-20 rounded border border-slate-700 bg-white p-1" title={`${member} ${sh.code}`}>
      <svg viewBox={`${-pad} ${-pad} ${sh.width + 2 * pad} ${sh.height + 2 * pad}`} className="w-full h-14">
        <polygon points={pts(sh.outline)} fill="#D9DEE4" stroke="#000" strokeWidth={Math.max(sh.width, sh.height) / 120} />
        {(sh.chambers || []).map((c, i) => <polygon key={i} points={pts(c)} fill="#fff" stroke="#000" strokeWidth={Math.max(sh.width, sh.height) / 200} />)}
        {(sh.thermal_break || []).map((c, i) => <polygon key={`t${i}`} points={pts(c)} fill="#374151" />)}
      </svg>
      <p className="text-[9px] font-mono text-slate-700 text-center truncate">{member}{sh.code ? ` · ${sh.code}` : ""}</p>
    </div>
  );
};

const SeriesRow = ({ s, onChange, idx, cid }) => {
  const set = (k, v) => onChange({ ...s, [k]: v });
  const num = (k) => <input data-testid={`cat-${cid}-${idx}-${k}`} type="number" value={s[k]} onChange={(e) => set(k, +e.target.value || 0)} className={inp} />;
  return (
    <div className="p-2.5 rounded-md border border-slate-800 bg-[#0B0F19]/60 space-y-2" data-testid={`cat-series-${cid}-${idx}`}>
      <div className="grid grid-cols-[1fr_repeat(4,58px)] gap-1.5 items-end">
        <input data-testid={`cat-${cid}-${idx}-series`} value={s.series} onChange={(e) => set("series", e.target.value)} className={`${inp} font-sans text-xs`} />
        {num("frame")}{num("mullion")}{num("sash")}{num("depth")}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {CODE_KEYS.map(([k, l]) => (
          <input key={k} data-testid={`cat-${cid}-${idx}-code-${k}`} placeholder={`${l} code`} value={s.codes?.[k] || ""}
            onChange={(e) => set("codes", { ...s.codes, [k]: e.target.value })} className={inp} />
        ))}
      </div>
      {s.shapes && Object.keys(s.shapes).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`cat-${cid}-${idx}-shapes`}>
          {Object.entries(s.shapes).map(([k, sh]) => <ShapeThumb key={k} member={k} sh={sh} />)}
        </div>
      )}
      <p className="text-[10px] text-slate-500">{s.kind} · {s.typologies?.join(", ") || "no typology"}{s.glass_range ? ` · glass ${s.glass_range}` : ""}{s.page ? ` · p.${s.page}` : ""}</p>
    </div>
  );
};

const CatalogueCard = ({ c, reload }) => {
  const [series, setSeries] = useState(c.series);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/catalogues/${c.catalogue_id}`, { series }); await reload(); toast.success("Catalogue saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const shapes = async () => {
    try { await api.post(`/catalogues/${c.catalogue_id}/shapes`); await reload(); toast.success("AI is tracing profile cross-sections"); } catch (e) { toast.error(errMsg(e)); }
  };
  const details = async () => {
    try { await api.post(`/catalogues/${c.catalogue_id}/details`); await reload(); toast.success("AI is locating the section drawings"); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete catalogue "${c.filename}"?`)) return;
    await api.delete(`/catalogues/${c.catalogue_id}`); await reload(); toast.success("Catalogue deleted");
  };
  return (
    <div className="rounded-lg border border-slate-700 p-3 space-y-2" data-testid={`catalogue-${c.catalogue_id}`}>
      <div className="flex items-center gap-2">
        <BookOpen size={15} className="text-cyan-400" />
        <span className="text-sm font-semibold truncate">{c.filename}</span>
        <span className="font-mono text-[10px] uppercase text-slate-400" data-testid={`catalogue-status-${c.catalogue_id}`}>
          {c.status === "processing" ? "AI reading…" : c.status === "ready" ? `${c.series.length} series` : c.status}
        </span>
        <div className="ml-auto flex gap-1">
          {c.status === "ready" && (
            <button data-testid={`catalogue-shapes-${c.catalogue_id}`} onClick={shapes} disabled={c.shapes_status === "processing"}
              className="h-7 px-2 rounded text-xs flex items-center gap-1 border border-slate-700 hover:border-cyan-500 hover:text-cyan-300 disabled:opacity-50">
              {c.shapes_status === "processing" ? <Loader2 size={12} className="animate-spin" /> : <Shapes size={12} />}{c.shapes_status === "ready" ? "Re-trace shapes" : "Trace profile shapes"}
            </button>
          )}
          {c.status === "ready" && (
            <button data-testid={`catalogue-details-${c.catalogue_id}`} onClick={details} disabled={c.details_status === "processing"}
              className="h-7 px-2 rounded text-xs flex items-center gap-1 border border-slate-700 hover:border-cyan-500 hover:text-cyan-300 disabled:opacity-50">
              {c.details_status === "processing" ? <Loader2 size={12} className="animate-spin" /> : <Images size={12} />}{c.details_status === "ready" ? "Re-scan sections" : "Find section drawings"}
            </button>
          )}
          {c.status === "ready" && <button data-testid={`catalogue-save-${c.catalogue_id}`} onClick={save} disabled={busy} className="h-7 px-2 rounded text-xs flex items-center gap-1 border border-slate-700 hover:border-cyan-500 hover:text-cyan-300"><Save size={12} />Save</button>}
          <button data-testid={`catalogue-delete-${c.catalogue_id}`} onClick={del} className="h-7 w-7 grid place-items-center rounded text-slate-400 hover:text-rose-400"><Trash2 size={13} /></button>
        </div>
      </div>
      {c.status === "processing" && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 size={13} className="animate-spin text-cyan-400" />Extracting face widths, depths and profile codes — large catalogues take a few minutes.</div>}
      {c.shapes_status && (
        <p className={`text-[11px] ${c.shapes_status === "failed" ? "text-rose-300" : c.shapes_status === "empty" ? "text-amber-300" : "text-slate-400"}`} data-testid={`catalogue-shapes-status-${c.catalogue_id}`}>
          {c.shapes_status === "processing" ? "Tracing profile cross-sections from the catalogue drawings… (1–3 min)"
            : c.shapes_status === "ready" ? `${c.shapes_count} profile shapes traced — used in section details`
            : c.shapes_status === "empty" ? "No profile cross-section drawings found in this PDF" : `Shape tracing failed: ${c.shapes_error}`}
        </p>
      )}
      {c.details_status && (
        <p className={`text-[11px] ${c.details_status === "failed" ? "text-rose-300" : c.details_status === "empty" ? "text-amber-300" : "text-slate-400"}`} data-testid={`catalogue-details-status-${c.catalogue_id}`}>
          {c.details_status === "processing" ? "Locating and cutting out the section drawings for each window/door combination… (1–3 min)"
            : c.details_status === "ready" ? `${c.details.length} exact section drawings found — auto-matched to each item's combination`
            : c.details_status === "empty" ? "No section drawings found in this PDF" : `Section drawing scan failed: ${c.details_error}`}
        </p>
      )}
      {c.status === "ready" && <CatalogueDetails c={c} reload={reload} />}
      {c.status === "failed" && <p className="text-xs text-rose-300 flex items-center gap-1.5"><AlertTriangle size={12} />{c.error}</p>}
      {c.status === "empty" && <p className="text-xs text-amber-300">No series data found in this PDF.</p>}
      {c.status === "ready" && (
        <>
          <div className="grid grid-cols-[1fr_repeat(4,58px)] gap-1.5 px-2.5 font-mono text-[9px] uppercase text-slate-500"><span>Series</span><span>Frame</span><span>Mullion</span><span>Sash</span><span>Depth</span></div>
          {series.map((s, i) => <SeriesRow key={i} idx={i} cid={c.catalogue_id} s={s} onChange={(v) => setSeries(series.map((x, j) => (j === i ? v : x)))} />)}
        </>
      )}
    </div>
  );
};

const BHORUKA = "https://bhorukaextrusions.com/wp-content/uploads/2025/02/IWIN-Aluminium-Doors-and-Windows_Bhoruka.pdf";

export const CatalogueDialog = ({ onClose }) => {
  const { catalogues, reload } = useCatalogues();
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const importUrl = async (u) => {
    if (!u) return;
    setUploading(true);
    try { await api.post("/catalogues/import-url", { url: u }); await reload(); setUrl(""); toast.success("Catalogue downloaded — AI is reading series data and section drawings"); }
    catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };
  const input = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please upload a PDF catalogue");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/catalogues/upload", fd); await reload();
      toast.success("Catalogue uploaded — AI is reading the technical data");
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-[#111827] border-slate-700 text-slate-100" data-testid="catalogue-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">YKK AP technical catalogue</DialogTitle>
          <DialogDescription className="text-slate-400">Upload the YKK AP catalogue PDF. Face widths, depths and profile codes found in it replace the built-in approximate values, and the codes appear in cut lists and section details.</DialogDescription>
        </DialogHeader>
        <button data-testid="catalogue-upload-zone" onClick={() => input.current?.click()} disabled={uploading}
          className="w-full p-5 rounded-lg border-2 border-dashed border-slate-700 hover:border-cyan-500 flex items-center gap-4 transition-colors text-left">
          {uploading ? <Loader2 className="animate-spin text-cyan-400" /> : <UploadCloud className="text-cyan-400" />}
          <span><span className="block text-sm font-semibold">{uploading ? "Uploading…" : "Upload catalogue PDF"}</span><span className="block text-xs text-slate-400">Up to 30 MB · values can be reviewed and corrected below</span></span>
        </button>
        <input ref={input} type="file" accept="application/pdf,.pdf" hidden data-testid="catalogue-file-input" onChange={(e) => upload(e.target.files[0])} />
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 h-9 px-2 rounded-md border border-slate-700 bg-[#0B0F19] focus-within:border-cyan-500">
            <Link2 size={14} className="text-slate-500" />
            <input data-testid="catalogue-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste a catalogue PDF link"
              className="flex-1 bg-transparent text-xs text-slate-100 focus:outline-none" />
          </div>
          <button data-testid="catalogue-url-import-btn" onClick={() => importUrl(url.trim())} disabled={uploading || !url.trim()}
            className="h-9 px-3 rounded-md bg-cyan-500 text-slate-950 text-xs font-semibold hover:bg-cyan-400 disabled:opacity-40">Import</button>
        </div>
        <button data-testid="catalogue-import-bhoruka-btn" onClick={() => importUrl(BHORUKA)} disabled={uploading}
          className="justify-self-start text-[11px] px-2.5 py-1 rounded-full border border-slate-700 text-slate-300 hover:border-cyan-500 hover:text-cyan-300 disabled:opacity-40">
          + Bhoruka Extrusions IWIN catalogue (YKK AP company)
        </button>
        <div className="space-y-3">
          {catalogues.length === 0 && <p className="text-xs text-slate-500" data-testid="no-catalogues">No catalogue uploaded yet — built-in approximate YKK AP values are used.</p>}
          {catalogues.map((c) => <CatalogueCard key={`${c.catalogue_id}-${c.updated_at}-${c.status}`} c={c} reload={reload} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
